/** Trading cockpit, trading-safety settings and their messages. Each key must exist in both languages. */
export const cockpitUi = {
  en: {
    "pt.market": "Market",
    "ck.draw.title": "Draw",
    "ck.draw.select": "Select (cursor) — click a drawing to select it, drag it to move it, double-click a text to edit it",
    "ck.draw.hline": "Line",
    "ck.draw.zone": "Zone (rectangle)",
    "ck.draw.hintZone": "Drag a rectangle; its colour in the window under the tool: green buy zone, red sell zone, neutral",
    "ck.draw.color": "Colour of the zone",
    "ck.draw.colorGood": "Green — buy zone",
    "ck.draw.colorBad": "Red — sell zone",
    "ck.draw.colorNeutral": "Neutral — zone",
    "ck.draw.colorBlack": "Black",
    "ck.draw.colorWhite": "White",
    "ck.draw.textSize": "Font size",
    "ck.draw.textSizePx": "{size} px",
    "ck.draw.trend": "Trend line",
    "ck.draw.fib": "Fib retracement",
    "ck.draw.fibExt": "Fib extension",
    "ck.draw.fibTrend": "Trend-based Fib extension",
    "ck.draw.fibTime": "Fib time zones",
    "ck.draw.hintFib": "Drag from the start to the end of the move",
    "ck.draw.hintFibExt": "Drag over the pullback (e.g. high → low): targets beyond its start",
    "ck.draw.hintFibTrend": "1/2: drag from the start to the end of the move",
    "ck.draw.hintFibTrend2": "2/2: click the end of the correction",
    "ck.draw.hintFibTime": "Drag one unit (e.g. swing to swing): lines at 1, 2, 3, 5, 8 … units",
    "ck.draw.tagExt": "Ext",
    "ck.draw.tagTrend": "Trend ext",
    "ck.draw.hray": "Horizontal ray",
    "ck.draw.vline": "Vertical line",
    "ck.draw.position": "Long / short position",
    "ck.draw.range": "Measure",
    "ck.draw.hintRay": "Click a price: a line from there to the right",
    "ck.draw.hintVline": "Click a point in time (e.g. news, session open)",
    "ck.draw.hintPosition": "1/2: drag from the entry to the stop (stop below = long)",
    "ck.draw.hintPosition2": "2/2: click the target (beyond the entry)",
    "ck.draw.hintRange": "Drag: price distance, percentage and time",
    "ck.draw.deleteSelected": "Delete the selected drawing (Del)",
    "ck.draw.selectedHint": "Drawing selected · Del or the trash deletes it · Esc ends",
    "ck.draw.pen": "Pen",
    "ck.draw.hintPen": "Hold and draw freely; the colour in the window under the tool",
    "ck.draw.text": "Text",
    "ck.draw.hintText": "Drag out the text box, then type right in it · Enter takes it, Shift+Enter a new line, Esc cancels; the colour in the window under the tool",
    "ck.draw.selectedTextHint": "Text selected · double-click or Enter edits it in place, the corners resize it · Del or the trash deletes it · Esc ends",
    "ck.draw.posTarget": "{side} · target {pct} · R/R 1:{rr}",
    "ck.draw.posStop": "Stop {pct}",
    "ck.draw.rangeLabel": "{diff} · {pct} · {time}",
    "ck.draw.undo": "Undo last drawing",
    "ck.draw.clear": "Delete all drawings",
    "ck.draw.lock": "Lock the drawings (no drawing, selecting, moving, changing or deleting)",
    "ck.draw.lockedHint": "The drawings are locked — unlock them to draw",
    "ck.draw.unlock": "Unlock the drawings",
    "ck.draw.clearConfirm": "Delete all your drawings on this market?",
    "ck.draw.hint1": "Click a price in the chart",
    "ck.draw.hintDrag": "Press and drag in the chart",
    "ck.draw.myLine": "My line",
    "ck.chart.fibConfluence": "Confluence {name}",

    "ck.bidAsk": "Bid {bid} · Ask {ask}",
    "ck.spread": "Spread {v} bp",
    "ck.live": "● live",
    "ck.delayed": "● delayed",
    "ck.delayedBy": "● delayed (~{m} min)",

    "ck.chart.title": "Chart · {tf}",
    "ck.chart.tp": "TP{n}",
    "ck.chart.liquidity": "Liquidity",
    "ck.chart.fvg": "FVG",
    "ck.chart.breaker": "Breaker",
    "ck.chart.poi.orderBlock": "POI · OB",
    "ck.chart.poi.fvg": "POI · FVG",
    "ck.chart.demand": "Demand",
    "ck.chart.fibCluster": "Fib cluster",
    "ck.chart.supply": "Supply",
    "ck.chart.premium": "Premium",
    "ck.chart.equilibrium": "Equilibrium",
    "ck.chart.discount": "Discount",
    "ck.event.BOS": "BOS",
    "ck.event.CHOCH": "CHoCH",
    "ck.event.SWEEP": "Sweep",
    "ck.eventHint.BOS.up": "Break above the last high: the rise carries on",
    "ck.chart.ifvg": "iFVG",
    "ck.chart.ote": "OTE 62–79 %",
    "ck.event.XBOS": "BOS ext.",
    "ck.event.XCHOCH": "CHoCH ext.",
    "ck.eventHint.XBOS.up": "External structure: break above the last large swing high, the higher trend carries on",
    "ck.eventHint.XBOS.down": "External structure: break below the last large swing low, the higher trend carries on",
    "ck.eventHint.XCHOCH.up": "External structure: first break against the higher downtrend — a change of trend confirmed",
    "ck.eventHint.XCHOCH.down": "External structure: first break against the higher uptrend — a change of trend confirmed",
    "ck.swingHint.HH": "Higher high: above the swing high before",
    "ck.swingHint.LH": "Lower high: below (or at) the swing high before",
    "ck.swingHint.HL": "Higher low: above the swing low before",
    "ck.swingHint.LL": "Lower low: below (or at) the swing low before",
    "ck.double.top": "Double Top",
    "ck.double.bottom": "Double Bottom",
    "ck.double.confirmed": "Neckline ✓",
    "ck.doubleHint.top": "Two highs at the same level (equal highs), the neckline at the low between them — confirmed only by a close below it",
    "ck.doubleHint.bottom": "Two lows at the same level (equal lows), the neckline at the high between them — confirmed only by a close above it",
    "ck.doubleHint.confirmed": "A close through the neckline: the double top / bottom is confirmed",
    "ck.triple.top": "Triple Top",
    "ck.triple.bottom": "Triple Bottom",
    "ck.tripleHint.top": "Three highs at the same level (equal highs), the neckline at the lowest low between them — confirmed only by a close below it",
    "ck.tripleHint.bottom": "Three lows at the same level (equal lows), the neckline at the highest high between them — confirmed only by a close above it",
    "ck.hs.top": "H&S",
    "ck.hs.bottom": "Inv. H&S",
    "ck.hsHint.top": "Head & shoulders: a head above two shoulders of similar height, the neckline through the lows between — confirmed only by a close below it",
    "ck.hsHint.bottom": "Inverse head & shoulders: a head below two shoulders of similar depth, the neckline through the highs between — confirmed only by a close above it",
    "ck.hsHint.confirmed": "A close through the neckline: the head & shoulders is confirmed",
    "ck.triangle.ascending": "Ascending triangle",
    "ck.triangle.descending": "Descending triangle",
    "ck.triangle.symmetric": "Symmetrical triangle",
    "ck.triangleHint.ascending": "Ascending triangle: flat highs over rising lows — buyers press against a ceiling",
    "ck.triangleHint.descending": "Descending triangle: flat lows under falling highs — sellers press against a floor",
    "ck.triangleHint.symmetric": "Symmetrical triangle: falling highs over rising lows — the range narrows, the breakout decides",
    "ck.triangle.up": "Breakout ↑",
    "ck.triangle.down": "Breakout ↓",
    "ck.triangleHint.breakout": "The first close outside the triangle: its breakout in this direction",
    "ck.triangle.risingWedge": "Rising wedge",
    "ck.triangle.fallingWedge": "Falling wedge",
    "ck.triangleHint.risingWedge": "Rising wedge: highs and lows rising, the lows faster — the rise loses room, often a break down",
    "ck.triangleHint.fallingWedge": "Falling wedge: highs and lows falling, the highs faster — the fall loses room, often a break up",
    "ck.session.tokyo": "Asia",
    "ck.session.london": "London",
    "ck.session.sweepHigh": "{session} high swept",
    "ck.session.sweepLow": "{session} low swept",
    "ck.session.breakHigh": "{session} high broken",
    "ck.session.breakLow": "{session} low broken",
    "ck.sessionHint.range": "The session's range: its high and low are the stops the next sessions go for",
    "ck.sessionHint.sweep": "A wick beyond the session's high / low and the close back inside: its stops taken, often a turn",
    "ck.sessionHint.break": "A close beyond the session's high / low: the range broken, the move goes on",
    "ck.eq.high": "EQH",
    "ck.eq.low": "EQL",
    "ck.eq.highTaken": "EQH taken",
    "ck.eq.lowTaken": "EQL taken",
    "ck.eqHint.high": "Equal highs: two highs at the same level — the buy stops resting above them",
    "ck.eqHint.low": "Equal lows: two lows at the same level — the sell stops resting below them",
    "ck.eqHint.taken": "Price went beyond the equal highs / lows: the liquidity there was taken",
    "ck.htfPoi": "HTF POI",
    "ck.htfChain": "HTF ✓",
    "ck.htfChainHint": "Confirmation on the lower timeframe: the entry chain formed inside a point of interest of the higher timeframe (1H, on the 1H chart 4H)",
    "ck.idm": "IDM",
    "ck.idmTaken": "IDM ✓",
    "ck.idmHint": "Inducement: the swing before the point of interest that lures early entries — its stops are the fuel",
    "ck.idmTakenHint": "The inducement was taken before the POI: the early entries are out, the POI is the cleaner entry",
    "ck.osc.rsi.up": "RSI < 30",
    "ck.osc.rsi.down": "RSI > 70",
    "ck.osc.stoch.up": "Stoch ↑",
    "ck.osc.stoch.down": "Stoch ↓",
    "ck.osc.macd.up": "MACD ↑",
    "ck.osc.macd.down": "MACD ↓",
    "ck.oscHint.rsi.up": "RSI (14) below 30: oversold — a warning the fall may pause",
    "ck.oscHint.rsi.down": "RSI (14) above 70: overbought — a warning the rise may pause",
    "ck.oscHint.stoch.up": "Stochastic (14/3/3) %K crosses %D upward below 20: a turn up out of oversold",
    "ck.oscHint.stoch.down": "Stochastic (14/3/3) %K crosses %D downward above 80: a turn down out of overbought",
    "ck.oscHint.macd.up": "MACD (12/26/9) crosses its signal line upward: momentum turns up",
    "ck.oscHint.macd.down": "MACD (12/26/9) crosses its signal line downward: momentum turns down",
    "ck.sideways": "Range",
    "ck.volume": "Vol ×{x}",
    "ck.volumeHint": "The break's bar traded at least 1.5 times the mean volume of the 20 bars before (on CFDs tick volume): participation behind it",
    "ck.trendReversal": "Trend turns at",
    "ck.trendline.confirmed": "Trendline ✓",
    "ck.trendlineHint.confirmed": "A third touch: one more swing on the line — the trendline counts as confirmed",
    "ck.trendline.broken": "Trendline ✗",
    "ck.trendlineHint.broken": "A close beyond the trendline: it is broken",
    "ck.div.bull": "Divergence ↑",
    "ck.div.bear": "Divergence ↓",
    "ck.div.hidden.bull": "Hidden divergence ↑",
    "ck.div.hidden.bear": "Hidden divergence ↓",
    "ck.divHint.bull": "Bullish divergence: a lower low, but a higher RSI (14) — the fall loses momentum",
    "ck.divHint.bear": "Bearish divergence: a higher high, but a lower RSI (14) — the rise loses momentum",
    "ck.divHint.hidden.bull": "Hidden bullish divergence: a higher low, but a lower RSI (14) — the uptrend goes on",
    "ck.divHint.hidden.bear": "Hidden bearish divergence: a lower high, but a higher RSI (14) — the downtrend goes on",
    "ck.cross.golden": "Golden Cross",
    "ck.cross.death": "Death Cross",
    "ck.crossHint.golden": "EMA 50 crosses above EMA 200: the long trend turns up",
    "ck.crossHint.death": "EMA 50 crosses below EMA 200: the long trend turns down",
    "ck.squeeze": "Squeeze",
    "ck.squeezeHint": "Bollinger's squeeze: the bands are as narrow as they have not been for 125 bars — calm before a move",
    "ck.squeeze.up": "Squeeze ↑",
    "ck.squeeze.down": "Squeeze ↓",
    "ck.squeezeHint.release": "The first close outside the bands after a squeeze: the move it was waiting for, in this direction",
    "ck.gap": "Price gap",
    "ck.pivot": "Pivot",
    "ck.displacement": "Displacement",
    "ck.displacementHint": "Impulse: a large body (at least one ATR, two thirds of the candle) that left a fair value gap",
    "ck.retest": "Retest ✓",
    "ck.retestHint": "Back at the level a BOS broke, and the close held on the new side: the level changed its role",
    "ck.killzone.london": "KZ London",
    "ck.killzone.newyork": "KZ New York",
    "ck.killzoneHint": "Killzone (New York time: London 02:00–05:00, New York 07:00–10:00): the box is the range traded in it",
    "ck.eventHint.BOS.down": "Break below the last low: the fall carries on",
    "ck.eventHint.CHOCH.up": "First break above a high after a fall: turn ↑",
    "ck.eventHint.CHOCH.down": "First break below a low after a rise: turn ↓",
    "ck.eventHint.SWEEP.up": "Wick below a low, close back: stops taken → ↑",
    "ck.eventHint.SWEEP.down": "Wick above a high, close back: stops taken → ↓",

    "ck.news.off": "No news source set up (Settings → Market data).",
    "ck.news.error": "The news source is currently unavailable — showing the archive.",
    "ck.news.none": "No relevant news in the last 48 hours.",
    "ck.news.disclaimer": "Headlines are classified by transparent rules (no language model).",
    "ck.impact.high": "High impact",
    "ck.impact.medium": "Medium impact",
    "ck.impact.low": "Low impact",


    "ck.pick.hint": "{field}: click into the chart",

    "ck.candle.doji": "Doji",
    "ck.candle.hammer": "Hammer",
    "ck.candle.hangingMan": "Hanging Man",
    "ck.candle.invertedHammer": "Inverted Hammer",
    "ck.candle.shootingStar": "Shooting Star",
    "ck.candle.bullEngulfing": "Bullish Engulfing",
    "ck.candle.bearEngulfing": "Bearish Engulfing",
    "ck.candle.morningStar": "Morning Star",
    "ck.candle.eveningStar": "Evening Star",
    "ck.candle.bullFakeout": "Fake breakout ↑",
    "ck.candle.bearFakeout": "Fake breakout ↓",
    "ck.candle.threeWhiteSoldiers": "Three White Soldiers",
    "ck.candle.threeBlackCrows": "Three Black Crows",
    "ck.chain.sweep": "① Sweep",
    "ck.chain.mss": "② MSS",
    "ck.chain.retest": "③ Retest",
    "ck.chain.entry": "④ Entry",
    "ck.chain.invalid": "✗ invalid",
    "ck.chainHint.sweep": "Liquidity taken: the swing was pierced, the bar closed back",
    "ck.chainHint.mss": "Market structure shift: a CHoCH the other way with a fair value gap",
    "ck.chainHint.retest": "Price is back in the gap",
    "ck.chainHint.entry.long": "LONG entry: a close out of the gap upwards — buy, the stop below the sweep's low",
    "ck.chainHint.entry.short": "SHORT entry: a close out of the gap downwards — sell, the stop above the sweep's high",
    "ck.chainHint.invalid": "A close beyond the sweep or the gap before the entry: the chain is void",
    "ck.candle.dragonflyDoji": "Dragonfly Doji",
    "ck.candle.gravestoneDoji": "Gravestone Doji",
    "ck.candle.spinningTop": "Spinning Top",
    "ck.candle.bullMarubozu": "Marubozu ↑",
    "ck.candle.bearMarubozu": "Marubozu ↓",
    "ck.candle.bullHarami": "Bullish Harami",
    "ck.candle.bearHarami": "Bearish Harami",
    "ck.candle.piercingLine": "Piercing Line",
    "ck.candle.darkCloudCover": "Dark Cloud Cover",
    "ck.candle.tweezerBottom": "Tweezer Bottom",
    "ck.candle.tweezerTop": "Tweezer Top",
    "ck.candle.insideBar": "Inside Bar",
    "ck.candle.bullOutsideBar": "Outside Bar ↑",
    "ck.candle.bearOutsideBar": "Outside Bar ↓",
    "ck.candleHint.doji": "Indecision: open ≈ close, shadows of similar length",
    "ck.candleHint.hammer": "Reversal ↑ in a downtrend: small body at the top, long lower wick",
    "ck.candleHint.hangingMan": "Reversal ↓ in an uptrend: small body at the top, long lower wick",
    "ck.candleHint.invertedHammer": "Reversal ↑ in a downtrend: small body at the bottom, long upper wick",
    "ck.candleHint.shootingStar": "Reversal ↓ in an uptrend: small body at the bottom, long upper wick",
    "ck.candleHint.bullEngulfing": "Reversal ↑ in a downtrend: a long green body engulfs the small red one before",
    "ck.candleHint.bearEngulfing": "Reversal ↓ in an uptrend: a long red body engulfs the small green one before",
    "ck.candleHint.morningStar": "Reversal ↑ in a downtrend: long red, small below it, long green",
    "ck.candleHint.eveningStar": "Reversal ↓ in an uptrend: long green, small above it, long red",
    "ck.candleHint.bullFakeout": "Break below a low taken back → ↑",
    "ck.candleHint.bearFakeout": "Break above a high taken back → ↓",
    "ck.candleHint.threeWhiteSoldiers": "Three long green candles, each higher: buyers in control",
    "ck.candleHint.threeBlackCrows": "Three long red candles, each lower: sellers in control",
    "ck.candleHint.dragonflyDoji": "Rejection ↑: open = close at the high, a long lower wick",
    "ck.candleHint.gravestoneDoji": "Rejection ↓: open = close at the low, a long upper wick",
    "ck.candleHint.spinningTop": "Balance: a small body, long wicks on both sides",
    "ck.candleHint.bullMarubozu": "Buyers in full control: a long green body without wicks",
    "ck.candleHint.bearMarubozu": "Sellers in full control: a long red body without wicks",
    "ck.candleHint.bullHarami": "Momentum fades ↑ in a downtrend: a small green body inside the long red one before",
    "ck.candleHint.bearHarami": "Momentum fades ↓ in an uptrend: a small red body inside the long green one before",
    "ck.candleHint.piercingLine": "Reversal ↑ in a downtrend: green opens below the red low, closes above its middle",
    "ck.candleHint.darkCloudCover": "Reversal ↓ in an uptrend: red opens above the green high, closes below its middle",
    "ck.candleHint.tweezerBottom": "Reversal ↑ in a downtrend: two candles with the same low",
    "ck.candleHint.tweezerTop": "Reversal ↓ in an uptrend: two candles with the same high",
    "ck.candleHint.insideBar": "Compression: the whole candle within the one before, a breakout follows",
    "ck.candleHint.bullOutsideBar": "Range expansion ↑: above the high and below the low before, the close in the upper third",
    "ck.candleHint.bearOutsideBar": "Range expansion ↓: above the high and below the low before, the close in the lower third",
  },
  de: {
    "pt.market": "Markt",
    "ck.draw.title": "Zeichnen",
    "ck.draw.select": "Auswählen (Mauszeiger) — Zeichnung anklicken zum Auswählen, ziehen zum Verschieben, Text doppelklicken zum Bearbeiten",
    "ck.draw.hline": "Linie",
    "ck.draw.zone": "Zone (Rechteck)",
    "ck.draw.hintZone": "Rechteck ziehen; Farbe im Fenster unter dem Werkzeug: grün Buy-Zone, rot Sell-Zone, neutral",
    "ck.draw.color": "Farbe der Zone",
    "ck.draw.colorGood": "Grün — Buy-Zone",
    "ck.draw.colorBad": "Rot — Sell-Zone",
    "ck.draw.colorNeutral": "Neutral — Zone",
    "ck.draw.colorBlack": "Schwarz",
    "ck.draw.colorWhite": "Weiß",
    "ck.draw.textSize": "Schriftgröße",
    "ck.draw.textSizePx": "{size} px",
    "ck.draw.trend": "Trendlinie",
    "ck.draw.fib": "Fib-Retracement",
    "ck.draw.fibExt": "Fib-Extension",
    "ck.draw.fibTrend": "Trend-Fib-Extension",
    "ck.draw.fibTime": "Fib-Zeitzonen",
    "ck.draw.hintFib": "Vom Beginn zum Ende der Bewegung ziehen",
    "ck.draw.hintFibExt": "Über die Korrektur ziehen (z. B. Hoch → Tief): Ziele jenseits ihres Beginns",
    "ck.draw.hintFibTrend": "1/2: vom Beginn zum Ende der Bewegung ziehen",
    "ck.draw.hintFibTrend2": "2/2: auf das Ende der Korrektur klicken",
    "ck.draw.hintFibTime": "Eine Einheit ziehen (z. B. Swing zu Swing): Linien bei 1, 2, 3, 5, 8 … Einheiten",
    "ck.draw.tagExt": "Ext",
    "ck.draw.tagTrend": "Trend-Ext",
    "ck.draw.hray": "Horizontaler Strahl",
    "ck.draw.vline": "Senkrechte Linie",
    "ck.draw.position": "Long- / Short-Position",
    "ck.draw.range": "Messen",
    "ck.draw.hintRay": "Klicke auf einen Preis: eine Linie ab dort nach rechts",
    "ck.draw.hintVline": "Klicke auf einen Zeitpunkt (z. B. News, Sitzungsbeginn)",
    "ck.draw.hintPosition": "1/2: vom Einstieg zum Stop ziehen (Stop darunter = Long)",
    "ck.draw.hintPosition2": "2/2: auf das Ziel klicken (jenseits des Einstiegs)",
    "ck.draw.hintRange": "Ziehen: Preisabstand, Prozent und Zeit",
    "ck.draw.deleteSelected": "Ausgewählte Zeichnung löschen (Entf)",
    "ck.draw.selectedHint": "Zeichnung ausgewählt · Entf oder Papierkorb löscht sie · Esc beendet",
    "ck.draw.pen": "Stift",
    "ck.draw.hintPen": "Gedrückt halten und frei zeichnen; Farbe im Fenster unter dem Werkzeug",
    "ck.draw.text": "Text",
    "ck.draw.hintText": "Textrahmen aufziehen, dann direkt hineinschreiben · Enter übernimmt, Umschalt+Enter neue Zeile, Esc bricht ab; Farbe im Fenster unter dem Werkzeug",
    "ck.draw.selectedTextHint": "Text ausgewählt · Doppelklick oder Enter bearbeitet ihn direkt, die Ecken ändern die Größe · Entf oder Papierkorb löscht ihn · Esc beendet",
    "ck.draw.posTarget": "{side} · Ziel {pct} · CRV 1:{rr}",
    "ck.draw.posStop": "Stop {pct}",
    "ck.draw.rangeLabel": "{diff} · {pct} · {time}",
    "ck.draw.undo": "Letzte Zeichnung rückgängig",
    "ck.draw.clear": "Alle Zeichnungen löschen",
    "ck.draw.lock": "Zeichnungen sperren (kein Zeichnen, Auswählen, Verschieben, Ändern oder Löschen)",
    "ck.draw.lockedHint": "Die Zeichnungen sind gesperrt — zum Zeichnen erst entsperren",
    "ck.draw.unlock": "Zeichnungen entsperren",
    "ck.draw.clearConfirm": "Alle deine Zeichnungen in diesem Markt löschen?",
    "ck.draw.hint1": "Klicke auf einen Preis im Chart",
    "ck.draw.hintDrag": "Im Chart drücken und ziehen",
    "ck.draw.myLine": "Meine Linie",
    "ck.chart.fibConfluence": "Konfluenz {name}",

    "ck.bidAsk": "Geld {bid} · Brief {ask}",
    "ck.spread": "Spread {v} BP",
    "ck.live": "● live",
    "ck.delayed": "● verzögert",
    "ck.delayedBy": "● verzögert (~{m} Min.)",

    "ck.chart.title": "Chart · {tf}",
    "ck.chart.tp": "TP{n}",
    "ck.chart.liquidity": "Liquidität",
    "ck.chart.fvg": "FVG",
    "ck.chart.breaker": "Breaker",
    "ck.chart.poi.orderBlock": "POI · OB",
    "ck.chart.poi.fvg": "POI · FVG",
    "ck.chart.demand": "Nachfrage",
    "ck.chart.fibCluster": "Fib-Cluster",
    "ck.chart.supply": "Angebot",
    "ck.chart.premium": "Premium",
    "ck.chart.equilibrium": "Equilibrium",
    "ck.chart.discount": "Discount",
    "ck.event.BOS": "BOS",
    "ck.event.CHOCH": "CHoCH",
    "ck.event.SWEEP": "Sweep",
    "ck.eventHint.BOS.up": "Bruch über das letzte Hoch: Anstieg setzt sich fort",
    "ck.chart.ifvg": "iFVG",
    "ck.chart.ote": "OTE 62–79 %",
    "ck.event.XBOS": "BOS ext.",
    "ck.event.XCHOCH": "CHoCH ext.",
    "ck.eventHint.XBOS.up": "Externe Struktur: Bruch über das letzte große Swing-Hoch, der übergeordnete Trend läuft weiter",
    "ck.eventHint.XBOS.down": "Externe Struktur: Bruch unter das letzte große Swing-Tief, der übergeordnete Trend läuft weiter",
    "ck.eventHint.XCHOCH.up": "Externe Struktur: erster Bruch gegen den übergeordneten Abwärtstrend — Trendwechsel bestätigt",
    "ck.eventHint.XCHOCH.down": "Externe Struktur: erster Bruch gegen den übergeordneten Aufwärtstrend — Trendwechsel bestätigt",
    "ck.swingHint.HH": "Higher High: über dem Swing-Hoch davor",
    "ck.swingHint.LH": "Lower High: unter (oder gleich) dem Swing-Hoch davor",
    "ck.swingHint.HL": "Higher Low: über dem Swing-Tief davor",
    "ck.swingHint.LL": "Lower Low: unter (oder gleich) dem Swing-Tief davor",
    "ck.double.top": "Double Top",
    "ck.double.bottom": "Double Bottom",
    "ck.double.confirmed": "Nackenlinie ✓",
    "ck.doubleHint.top": "Zwei Hochs auf gleicher Höhe (Equal Highs), die Nackenlinie am Tief dazwischen — bestätigt erst mit Schluss darunter",
    "ck.doubleHint.bottom": "Zwei Tiefs auf gleicher Höhe (Equal Lows), die Nackenlinie am Hoch dazwischen — bestätigt erst mit Schluss darüber",
    "ck.doubleHint.confirmed": "Schluss durch die Nackenlinie: Double Top / Bottom bestätigt",
    "ck.triple.top": "Triple Top",
    "ck.triple.bottom": "Triple Bottom",
    "ck.tripleHint.top": "Drei Hochs auf gleicher Höhe (Equal Highs), die Nackenlinie am tiefsten Tief dazwischen — bestätigt erst mit Schluss darunter",
    "ck.tripleHint.bottom": "Drei Tiefs auf gleicher Höhe (Equal Lows), die Nackenlinie am höchsten Hoch dazwischen — bestätigt erst mit Schluss darüber",
    "ck.hs.top": "H&S",
    "ck.hs.bottom": "Inv. H&S",
    "ck.hsHint.top": "Head & Shoulders: ein Kopf über zwei etwa gleich hohen Schultern, die Nackenlinie durch die Tiefs dazwischen — bestätigt erst mit Schluss darunter",
    "ck.hsHint.bottom": "Inverse Head & Shoulders: ein Kopf unter zwei etwa gleich tiefen Schultern, die Nackenlinie durch die Hochs dazwischen — bestätigt erst mit Schluss darüber",
    "ck.hsHint.confirmed": "Schluss durch die Nackenlinie: Head & Shoulders bestätigt",
    "ck.triangle.ascending": "Aufsteigendes Dreieck",
    "ck.triangle.descending": "Absteigendes Dreieck",
    "ck.triangle.symmetric": "Symmetrisches Dreieck",
    "ck.triangleHint.ascending": "Aufsteigendes Dreieck: flache Hochs über steigenden Tiefs — Käufer drücken gegen eine Decke",
    "ck.triangleHint.descending": "Absteigendes Dreieck: flache Tiefs unter fallenden Hochs — Verkäufer drücken gegen einen Boden",
    "ck.triangleHint.symmetric": "Symmetrisches Dreieck: fallende Hochs über steigenden Tiefs — die Spanne wird enger, der Ausbruch entscheidet",
    "ck.triangle.up": "Ausbruch ↑",
    "ck.triangle.down": "Ausbruch ↓",
    "ck.triangleHint.breakout": "Der erste Schluss außerhalb des Dreiecks: sein Ausbruch in diese Richtung",
    "ck.triangle.risingWedge": "Steigender Keil",
    "ck.triangle.fallingWedge": "Fallender Keil",
    "ck.triangleHint.risingWedge": "Steigender Keil: Hochs und Tiefs steigen, die Tiefs schneller — dem Anstieg geht der Raum aus, oft ein Ausbruch nach unten",
    "ck.triangleHint.fallingWedge": "Fallender Keil: Hochs und Tiefs fallen, die Hochs schneller — dem Fall geht der Raum aus, oft ein Ausbruch nach oben",
    "ck.session.tokyo": "Asien",
    "ck.session.london": "London",
    "ck.session.sweepHigh": "{session}-Hoch geholt",
    "ck.session.sweepLow": "{session}-Tief geholt",
    "ck.session.breakHigh": "{session}-Hoch gebrochen",
    "ck.session.breakLow": "{session}-Tief gebrochen",
    "ck.sessionHint.range": "Die Spanne der Sitzung: ihr Hoch und Tief sind die Stops, auf die die nächsten Sitzungen zielen",
    "ck.sessionHint.sweep": "Ein Docht über das Hoch / unter das Tief der Sitzung und der Schluss zurück: ihre Stops abgeholt, oft eine Wende",
    "ck.sessionHint.break": "Ein Schluss jenseits von Hoch / Tief der Sitzung: die Spanne gebrochen, die Bewegung läuft weiter",
    "ck.eq.high": "EQH",
    "ck.eq.low": "EQL",
    "ck.eq.highTaken": "EQH geholt",
    "ck.eq.lowTaken": "EQL geholt",
    "ck.eqHint.high": "Equal Highs: zwei Hochs auf gleicher Höhe — darüber ruhen die Kauf-Stops",
    "ck.eqHint.low": "Equal Lows: zwei Tiefs auf gleicher Höhe — darunter ruhen die Verkaufs-Stops",
    "ck.eqHint.taken": "Der Kurs lief über die Equal Highs / unter die Equal Lows: die Liquidität dort ist abgeholt",
    "ck.htfPoi": "HTF-POI",
    "ck.htfChain": "HTF ✓",
    "ck.htfChainHint": "Bestätigung auf der niedrigen Zeitebene: die Entry-Kette entstand in einem POI der höheren Zeitebene (1H, im 1H-Chart 4H)",
    "ck.idm": "IDM",
    "ck.idmTaken": "IDM ✓",
    "ck.idmHint": "Inducement: der Swing vor dem POI, der zu frühen Einstiegen verleitet — seine Stops sind der Treibstoff",
    "ck.idmTakenHint": "Das Inducement wurde vor dem POI abgeholt: die frühen Einstiege sind raus, der POI ist der sauberere Einstieg",
    "ck.osc.rsi.up": "RSI < 30",
    "ck.osc.rsi.down": "RSI > 70",
    "ck.osc.stoch.up": "Stoch ↑",
    "ck.osc.stoch.down": "Stoch ↓",
    "ck.osc.macd.up": "MACD ↑",
    "ck.osc.macd.down": "MACD ↓",
    "ck.oscHint.rsi.up": "RSI (14) unter 30: überverkauft — ein Hinweis, dass der Fall pausieren könnte",
    "ck.oscHint.rsi.down": "RSI (14) über 70: überkauft — ein Hinweis, dass der Anstieg pausieren könnte",
    "ck.oscHint.stoch.up": "Stochastik (14/3/3): %K kreuzt %D unter 20 nach oben — eine Wende aus dem überverkauften Bereich",
    "ck.oscHint.stoch.down": "Stochastik (14/3/3): %K kreuzt %D über 80 nach unten — eine Wende aus dem überkauften Bereich",
    "ck.oscHint.macd.up": "MACD (12/26/9) kreuzt seine Signallinie nach oben: das Momentum dreht nach oben",
    "ck.oscHint.macd.down": "MACD (12/26/9) kreuzt seine Signallinie nach unten: das Momentum dreht nach unten",
    "ck.sideways": "Range",
    "ck.volume": "Vol ×{x}",
    "ck.volumeHint": "Die Bruchkerze handelte mindestens das 1,5-Fache des mittleren Volumens der 20 Kerzen davor (bei CFDs Tick-Volumen): Beteiligung dahinter",
    "ck.trendReversal": "Trendwende bei",
    "ck.trendline.confirmed": "Trendlinie ✓",
    "ck.trendlineHint.confirmed": "Dritte Berührung: ein weiterer Swing auf der Linie — die Trendlinie gilt als bestätigt",
    "ck.trendline.broken": "Trendlinie ✗",
    "ck.trendlineHint.broken": "Schluss jenseits der Trendlinie: sie ist gebrochen",
    "ck.div.bull": "Divergenz ↑",
    "ck.div.bear": "Divergenz ↓",
    "ck.div.hidden.bull": "Versteckte Divergenz ↑",
    "ck.div.hidden.bear": "Versteckte Divergenz ↓",
    "ck.divHint.bull": "Bullische Divergenz: ein tieferes Tief, aber ein höherer RSI (14) — dem Fall geht die Kraft aus",
    "ck.divHint.bear": "Bärische Divergenz: ein höheres Hoch, aber ein tieferer RSI (14) — dem Anstieg geht die Kraft aus",
    "ck.divHint.hidden.bull": "Versteckte bullische Divergenz: ein höheres Tief, aber ein tieferer RSI (14) — der Aufwärtstrend läuft weiter",
    "ck.divHint.hidden.bear": "Versteckte bärische Divergenz: ein tieferes Hoch, aber ein höherer RSI (14) — der Abwärtstrend läuft weiter",
    "ck.cross.golden": "Golden Cross",
    "ck.cross.death": "Death Cross",
    "ck.crossHint.golden": "EMA 50 kreuzt über EMA 200: der lange Trend dreht nach oben",
    "ck.crossHint.death": "EMA 50 kreuzt unter EMA 200: der lange Trend dreht nach unten",
    "ck.squeeze": "Squeeze",
    "ck.squeezeHint": "Bollingers Squeeze: die Bänder so eng wie seit 125 Kerzen nicht — Ruhe vor einer Bewegung",
    "ck.squeeze.up": "Squeeze ↑",
    "ck.squeeze.down": "Squeeze ↓",
    "ck.squeezeHint.release": "Der erste Schluss außerhalb der Bänder nach einem Squeeze: die erwartete Bewegung, in diese Richtung",
    "ck.gap": "Kurslücke",
    "ck.pivot": "Pivot",
    "ck.displacement": "Displacement",
    "ck.displacementHint": "Impuls: großer Körper (mindestens eine ATR, zwei Drittel der Kerze), der ein Fair Value Gap hinterließ",
    "ck.retest": "Retest ✓",
    "ck.retestHint": "Zurück am Level, das ein BOS gebrochen hat, und der Schluss hielt auf der neuen Seite: Rollenwechsel",
    "ck.killzone.london": "KZ London",
    "ck.killzone.newyork": "KZ New York",
    "ck.killzoneHint": "Killzone (New-York-Zeit: London 02:00–05:00, New York 07:00–10:00): die Box ist die Spanne, die darin gehandelt wurde",
    "ck.eventHint.BOS.down": "Bruch unter das letzte Tief: Abwärts setzt sich fort",
    "ck.eventHint.CHOCH.up": "Erster Bruch über ein Hoch nach Abwärts: Wende ↑",
    "ck.eventHint.CHOCH.down": "Erster Bruch unter ein Tief nach Aufwärts: Wende ↓",
    "ck.eventHint.SWEEP.up": "Docht unter ein Tief, Schluss zurück: Stops abgeholt → ↑",
    "ck.eventHint.SWEEP.down": "Docht über ein Hoch, Schluss zurück: Stops abgeholt → ↓",

    "ck.news.off": "Keine News-Quelle eingerichtet (Einstellungen → Marktdaten).",
    "ck.news.error": "Die News-Quelle ist gerade nicht erreichbar — angezeigt wird das Archiv.",
    "ck.news.none": "Keine relevanten Nachrichten in den letzten 48 Stunden.",
    "ck.news.disclaimer": "Schlagzeilen werden mit transparenten Regeln eingeordnet (kein Sprachmodell).",
    "ck.impact.high": "Hoher Einfluss",
    "ck.impact.medium": "Mittlerer Einfluss",
    "ck.impact.low": "Geringer Einfluss",


    "ck.pick.hint": "{field}: in den Chart klicken",

    "ck.candle.doji": "Doji",
    "ck.candle.hammer": "Hammer",
    "ck.candle.hangingMan": "Hanging Man",
    "ck.candle.invertedHammer": "Umgek. Hammer",
    "ck.candle.shootingStar": "Shooting Star",
    "ck.candle.bullEngulfing": "Bullish Engulfing",
    "ck.candle.bearEngulfing": "Bearish Engulfing",
    "ck.candle.morningStar": "Morning Star",
    "ck.candle.eveningStar": "Evening Star",
    "ck.candle.bullFakeout": "Fake-Breakout ↑",
    "ck.candle.bearFakeout": "Fake-Breakout ↓",
    "ck.candle.threeWhiteSoldiers": "Drei weiße Soldaten",
    "ck.candle.threeBlackCrows": "Drei schwarze Krähen",
    "ck.chain.sweep": "① Sweep",
    "ck.chain.mss": "② MSS",
    "ck.chain.retest": "③ Retest",
    "ck.chain.entry": "④ Entry",
    "ck.chain.invalid": "✗ ungültig",
    "ck.chainHint.sweep": "Liquidität abgeholt: der Swing wurde durchstochen, Schluss zurück",
    "ck.chainHint.mss": "Strukturwechsel: ein CHoCH in die Gegenrichtung mit Fair Value Gap",
    "ck.chainHint.retest": "Der Preis ist zurück im Gap",
    "ck.chainHint.entry.long": "LONG-Einstieg: Schluss aus dem Gap nach oben — kaufen, der Stop unter dem Sweep-Tief",
    "ck.chainHint.entry.short": "SHORT-Einstieg: Schluss aus dem Gap nach unten — verkaufen, der Stop über dem Sweep-Hoch",
    "ck.chainHint.invalid": "Schluss jenseits des Sweeps oder des Gaps vor dem Einstieg: die Kette ist ungültig",
    "ck.candle.dragonflyDoji": "Dragonfly Doji",
    "ck.candle.gravestoneDoji": "Gravestone Doji",
    "ck.candle.spinningTop": "Spinning Top",
    "ck.candle.bullMarubozu": "Marubozu ↑",
    "ck.candle.bearMarubozu": "Marubozu ↓",
    "ck.candle.bullHarami": "Bullish Harami",
    "ck.candle.bearHarami": "Bearish Harami",
    "ck.candle.piercingLine": "Piercing Line",
    "ck.candle.darkCloudCover": "Dark Cloud Cover",
    "ck.candle.tweezerBottom": "Tweezer Bottom",
    "ck.candle.tweezerTop": "Tweezer Top",
    "ck.candle.insideBar": "Inside Bar",
    "ck.candle.bullOutsideBar": "Outside Bar ↑",
    "ck.candle.bearOutsideBar": "Outside Bar ↓",
    "ck.candleHint.doji": "Unentschlossenheit: Eröffnung ≈ Schluss, Dochte ähnlich lang",
    "ck.candleHint.hammer": "Umkehr ↑ im Abwärtstrend: kleiner Körper oben, langer Docht unten",
    "ck.candleHint.hangingMan": "Umkehr ↓ im Aufwärtstrend: kleiner Körper oben, langer Docht unten",
    "ck.candleHint.invertedHammer": "Umkehr ↑ im Abwärtstrend: kleiner Körper unten, langer Docht oben",
    "ck.candleHint.shootingStar": "Umkehr ↓ im Aufwärtstrend: kleiner Körper unten, langer Docht oben",
    "ck.candleHint.bullEngulfing": "Umkehr ↑ im Abwärtstrend: langer grüner Körper umschließt den kleinen roten davor",
    "ck.candleHint.bearEngulfing": "Umkehr ↓ im Aufwärtstrend: langer roter Körper umschließt den kleinen grünen davor",
    "ck.candleHint.morningStar": "Umkehr ↑ im Abwärtstrend: lang rot, klein darunter, lang grün",
    "ck.candleHint.eveningStar": "Umkehr ↓ im Aufwärtstrend: lang grün, klein darüber, lang rot",
    "ck.candleHint.bullFakeout": "Bruch unter ein Tief zurückgeholt → ↑",
    "ck.candleHint.bearFakeout": "Bruch über ein Hoch zurückgeholt → ↓",
    "ck.candleHint.threeWhiteSoldiers": "Drei lange grüne Kerzen, jede höher: Käufer dominieren",
    "ck.candleHint.threeBlackCrows": "Drei lange rote Kerzen, jede tiefer: Verkäufer dominieren",
    "ck.candleHint.dragonflyDoji": "Zurückweisung ↑: Eröffnung = Schluss am Hoch, langer Docht unten",
    "ck.candleHint.gravestoneDoji": "Zurückweisung ↓: Eröffnung = Schluss am Tief, langer Docht oben",
    "ck.candleHint.spinningTop": "Gleichgewicht: kleiner Körper, lange Dochte auf beiden Seiten",
    "ck.candleHint.bullMarubozu": "Käufer in voller Kontrolle: langer grüner Körper ohne Dochte",
    "ck.candleHint.bearMarubozu": "Verkäufer in voller Kontrolle: langer roter Körper ohne Dochte",
    "ck.candleHint.bullHarami": "Momentum lässt nach ↑ im Abwärtstrend: kleiner grüner Körper im langen roten davor",
    "ck.candleHint.bearHarami": "Momentum lässt nach ↓ im Aufwärtstrend: kleiner roter Körper im langen grünen davor",
    "ck.candleHint.piercingLine": "Umkehr ↑ im Abwärtstrend: grün eröffnet unter dem roten Tief, schließt über dessen Mitte",
    "ck.candleHint.darkCloudCover": "Umkehr ↓ im Aufwärtstrend: rot eröffnet über dem grünen Hoch, schließt unter dessen Mitte",
    "ck.candleHint.tweezerBottom": "Umkehr ↑ im Abwärtstrend: zwei Kerzen mit gleichem Tief",
    "ck.candleHint.tweezerTop": "Umkehr ↓ im Aufwärtstrend: zwei Kerzen mit gleichem Hoch",
    "ck.candleHint.insideBar": "Kompression: die ganze Kerze in der davor, ein Ausbruch folgt",
    "ck.candleHint.bullOutsideBar": "Spanne weitet sich ↑: über dem Hoch und unter dem Tief davor, Schluss im oberen Drittel",
    "ck.candleHint.bearOutsideBar": "Spanne weitet sich ↓: über dem Hoch und unter dem Tief davor, Schluss im unteren Drittel",
  },
};
